// Prepares recorded values for leaving the page. The result must survive both
// hops to the extension: `postMessage` (structured clone) and superjson in the
// content script. So only plain data plus Date, RegExp, Map, Set, bigint and
// undefined pass through; everything else becomes plain objects or markers.
// No dependencies: the extension imports this, and other apps copy it.
import type { TrpcDebugEvent } from './types'

/** `Object.prototype.toString` tag; works across realms, unlike `instanceof`. */
function tagOf(value: object): string {
	return Object.prototype.toString.call(value).slice(8, -1)
}

function isErrorLike(value: object): value is Error {
	return value instanceof Error || tagOf(value) === 'Error'
}

/** 'Blob' / 'File', or null. Some environments (happy-dom) leave their Blob untagged. */
function blobKind(value: object): 'Blob' | 'File' | null {
	const tag = tagOf(value)
	if (tag === 'Blob' || tag === 'File') return tag
	if (typeof File !== 'undefined' && value instanceof File) return 'File'
	if (typeof Blob !== 'undefined' && value instanceof Blob) return 'Blob'
	return null
}

function isPlainObject(value: object): boolean {
	const proto = Object.getPrototypeOf(value)
	// The last check accepts plain objects created in another realm (an iframe).
	return (
		proto === null ||
		proto === Object.prototype ||
		(tagOf(value) === 'Object' && proto.constructor?.name === 'Object')
	)
}

function ownProps(value: object, ancestors: Set<object>): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	for (const key of Object.keys(value)) {
		try {
			out[key] = convert((value as Record<string, unknown>)[key], ancestors)
		} catch {
			out[key] = '[unreadable]'
		}
	}
	return out
}

function convert(value: unknown, ancestors: Set<object>): unknown {
	switch (typeof value) {
		case 'string':
		case 'number':
		case 'boolean':
		case 'undefined':
		case 'bigint':
			return value
		case 'symbol':
			return value.toString()
		case 'function':
			return `ƒ ${value.name || 'anonymous'}()`
	}
	if (value === null) return null
	const obj = value as object
	if (ancestors.has(obj)) return '[Circular]'

	const tag = tagOf(obj)
	if (tag === 'Date') return new Date((obj as Date).getTime())
	if (tag === 'RegExp') return new RegExp((obj as RegExp).source, (obj as RegExp).flags)
	if (tag === 'URL') return String(obj)
	const blob = blobKind(obj)
	if (blob) {
		const { size, type } = obj as Blob
		const name = (obj as Partial<File>).name
		return `[${blob}${name ? ` ${name}` : ''}, ${size} B${type ? `, ${type}` : ''}]`
	}
	if (tag === 'ArrayBuffer' || ArrayBuffer.isView(obj)) {
		return `[${tag}(${(obj as ArrayBuffer).byteLength} B)]`
	}

	ancestors.add(obj)
	try {
		if (tag === 'Map') {
			return new Map(
				[...(obj as Map<unknown, unknown>)].map(([k, v]) => [
					convert(k, ancestors),
					convert(v, ancestors),
				]),
			)
		}
		if (tag === 'Set') {
			return new Set([...(obj as Set<unknown>)].map((v) => convert(v, ancestors)))
		}
		if (Array.isArray(obj)) return obj.map((v) => convert(v, ancestors))
		if (isErrorLike(obj)) return errorToPlain(obj, ancestors)

		const props = ownProps(obj, ancestors)
		if (isPlainObject(obj) || Object.keys(props).length > 0) return props
		// A class instance with nothing enumerable (a DOM node, a Response, …).
		return `[${obj.constructor?.name || tag}]`
	} finally {
		ancestors.delete(obj)
	}
}

function errorToPlain(error: Error, ancestors: Set<object>) {
	return {
		...ownProps(error, ancestors),
		name: error.name,
		message: error.message,
		stack: error.stack,
		...('cause' in error && error.cause !== undefined
			? { cause: convert(error.cause, ancestors) }
			: {}),
	}
}

/** Copy of `value` that survives structured clone and superjson. */
export function toCloneSafe(value: unknown): unknown {
	return convert(value, new Set())
}

/**
 * Event ready for `postMessage`. A recorded error becomes a plain object
 * with `name`, `message`, `stack` and its own fields (`data`, `shape`, … of a
 * `TRPCClientError`); the receiver rebuilds an `Error` from it.
 */
export function toCloneSafeEvent({ kind, record }: TrpcDebugEvent): TrpcDebugEvent {
	return {
		kind,
		record: {
			...record,
			input: toCloneSafe(record.input),
			result: toCloneSafe(record.result),
			error: toCloneSafe(record.error),
		},
	}
}
